import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import {
  CheckboxCard,
  Flex,
  Heading,
  Input,
  Notice,
  Text,
} from '@buffer-mono/popcorn'

import { ICON_COLORS, OPTION_EMOJIS_FALLBACK } from '../../constants'
import type { StepProps } from '../../types'
import { StepActions } from '../StepActions/StepActions'

import styles from './MultiSelectStep.module.css'

/**
 * @hardcoded Slug-to-emoji map. Adding or renaming an option in the API silently
 *   loses its icon on the frontend.
 * @fix Add `iconUrl` to `SurveyStepOption` in the GraphQL schema and render from
 *   the API value. Do not extend this map.
 */
const OPTION_EMOJIS: Record<string, string> = {
  none: '\u{1F4BB}',
  'social-media-management-tool': '\u{2692}\u{FE0F}',
  'specific-social-tools': '\u{270F}\u{FE0F}',
  'meta-business': '\u{1F535}',
  'ai-platforms': '\u{1F916}',
  other: '\u{1F984}',
}

export function MultiSelectStep({
  step,
  initialAnswers,
  initialTextValue,
  disabled,
  loading,
  error,
  onDismissError,
  onSubmit,
  onSkip,
}: StepProps): JSX.Element {
  const [selected, setSelected] = useState<string[]>(initialAnswers)
  const [otherText, setOtherText] = useState(initialTextValue ?? '')
  const otherInputRef = useRef<HTMLInputElement>(null)
  /**
   * @hardcoded Implicit contract on slug name 'other'.
   * @fix Add `allowsTextInput` to `SurveyStepOption` and replace this slug check.
   */
  const isOtherSelected = selected.includes('other')
  const isSubmitDisabled =
    selected.length === 0 || (isOtherSelected && !otherText.trim())

  useEffect(() => {
    if (isOtherSelected) {
      otherInputRef.current?.focus()
    }
  }, [isOtherSelected])

  const toggle = (slug: string, checked: boolean): void => {
    setSelected((prev) =>
      checked ? [...prev, slug] : prev.filter((s) => s !== slug),
    )
  }

  return (
    <div className={styles.step}>
      <Heading as="h1" size="xl" align="center">
        {step.title}
      </Heading>

      {error && (
        <Notice variant="error" onDismiss={onDismissError}>
          <Notice.Text>{error}</Notice.Text>
        </Notice>
      )}

      <CheckboxCard.Group className={styles.options}>
        {step.options.map((option, index) => {
          const emoji =
            OPTION_EMOJIS[option.slug] ??
            OPTION_EMOJIS_FALLBACK[index % OPTION_EMOJIS_FALLBACK.length]
          const isOther = option.slug === 'other'
          return (
            <CheckboxCard
              key={option.slug}
              padding="sm"
              className={styles.card}
              checked={selected.includes(option.slug)}
              onCheckedChange={(checked): void =>
                toggle(option.slug, Boolean(checked))
              }
              disabled={disabled}
            >
              <Flex direction="column" className={styles.cardContent}>
                <Flex align="center" gap="sm">
                  <div
                    className={styles.optionIconWrapper}
                    style={{
                      backgroundColor: ICON_COLORS[index % ICON_COLORS.length],
                    }}
                  >
                    <span className={styles.optionIcon} aria-hidden="true">
                      {emoji}
                    </span>
                  </div>
                  <Flex direction="column">
                    <span className={styles.cardLabel}>{option.label}</span>
                    {option.description && (
                      <Text color="subtle" size="sm">
                        {option.description}
                      </Text>
                    )}
                  </Flex>
                </Flex>
                {isOther && (
                  <div
                    className={styles.otherExpand}
                    data-open={isOtherSelected || undefined}
                  >
                    <div
                      className={styles.otherExpandInner}
                      onMouseDown={(e): void => e.stopPropagation()}
                      onClick={(e): void => e.stopPropagation()}
                      onKeyDown={(e): void => e.stopPropagation()}
                      role="presentation"
                    >
                      <Input
                        ref={otherInputRef}
                        placeholder="Please specify..."
                        value={otherText}
                        onChange={(e): void => setOtherText(e.target.value)}
                        disabled={disabled}
                      />
                    </div>
                  </div>
                )}
              </Flex>
            </CheckboxCard>
          )
        })}
      </CheckboxCard.Group>

      <StepActions
        onContinue={(): void =>
          onSubmit(selected, isOtherSelected ? otherText.trim() : undefined)
        }
        disabled={isSubmitDisabled}
        controlsDisabled={disabled}
        loading={loading}
        isSkippable={step.isSkippable}
        onSkip={onSkip}
      />
    </div>
  )
}
